<template>
  <ChartCard :data-widget-id="widgetId" :id-for-aria="widgetId">
    <ChartHeader
      :label="widgetLabel"
      :widget-download-id="widgetId"
      tooltip-key="dropoff-highest-proportion-by-stage"
    />
    <ChartContent
      height="311.5px"
      :empty-data="emptyData || noWidgetData"
      :error="error"
      :loading="loading"
      type="horizontalBarChart"
      class="px-3 pt-2 pb-0"
    >
      <BarChart :id="widgetId" :data="chartData" :options="chartOptions" :plugins="plugins" title="Stages at which most candidates dropdoff" />
    </ChartContent>
  </ChartCard>
</template>

<script setup>
import ChartHeader from '@/components/insights/Chart/ChartHeader.vue';
import ChartCard from '@/components/insights/Chart/ChartCard.vue';
import ChartContent from '@/components/insights/Chart/ChartContent.vue';
import BarChart from '@/components/insights/Chart/BarChart.vue';
import { computed } from 'vue';
import { useColors } from '@/composables/colors';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  dataLabelsConfig,
  flippedAxisTooltipPluginConfig,
  xScaleWithPercentFormatConfig,
  basicYScaleConfig,
} from '@/api/helpers/insights/commonChartConfigs';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  emptyData: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const widgetLabel = 'At what stage do most candidates drop off?';

const plugins = [ChartDataLabels];

const widgetId = 'higher-proportion-drop-offs';

const {
  stageScreen,
  stageAssessment,
  stageOffer,
  stageApplication,
} = useColors();

const noWidgetData = computed(() => !props.data
    || !props.data.net_dropoff_rate
    || Object.keys(props.data.net_dropoff_rate)
      .every((key) => !props.data.net_dropoff_rate[key]));

const chartData = computed(() => {
  const { net_dropoff_rate: netDropoffRate } = props.data || {};

  return {
    labels: ['Application', 'Screening', 'Assessment', 'Offered'],
    datasets: [
      {
        label: '',
        backgroundColor: [
          stageApplication.value,
          stageScreen.value,
          stageAssessment.value,
          stageOffer.value,
        ],
        borderColor: [
          stageApplication.value,
          stageScreen.value,
          stageAssessment.value,
          stageOffer.value,
        ],
        fill: false,
        maxBarThickness: 24,
        barThickness: 'flex',
        data: [
          netDropoffRate.review * 100,
          netDropoffRate.screen * 100,
          netDropoffRate.assessment * 100,
          netDropoffRate.offer * 100,
        ],
      },
    ],
  };
});

const chartOptions = computed(() => ({
  maintainAspectRatio: false,
  responsive: true,
  indexAxis: 'y',
  plugins: {
    legend: { display: false },
    tooltip: flippedAxisTooltipPluginConfig(chartData, widgetId),
    datalabels: dataLabelsConfig,
  },
  scales: {
    x: xScaleWithPercentFormatConfig,
    y: basicYScaleConfig,
  },
}));
</script>
